import { copy } from '@shared/copy';
import { formatNumber } from '@shared/format';
import { ChevronNextIcon, ChevronPreviousIcon } from '@shared/ui/icons';

/**
 * The traversal control, shared by every operator list that walks pages.
 *
 * Promoted to the shared ring on its **fourth** surface — the notification
 * inbox, the case queue, the account register, and the comment moderation queue
 * all walk a server-paged list, and a console that pages one way here and
 * another way there teaches the operator two habits for one act.
 *
 * **Two paging models, one control.** The services behind these lists do not
 * agree on how they page, and the console does not pretend they do: an offset
 * list knows its total and can therefore say «صفحة ٢ من ٧», while a keyset list
 * deliberately buys its speed by *not* running a count query — so it can say
 * which page it walked to and how many rows are on it, and nothing more. Pass
 * `pageCount` / `total` where the payload carries them and omit them where it
 * does not; a figure this component invented would be worse than the absence.
 *
 * **The ordinals are pressable only where an ordinal is addressable.** An offset
 * list resolves «page seven» from the number alone, so it is handed `onGoToPage`
 * and draws a numbered strip: the reader jumps rather than pressing «التالي»
 * six times. A keyset list resolves nothing from a number — page seven is
 * whatever the six cursors before it lead to — so it is handed no `onGoToPage`
 * and the strip is replaced by the ordinal it did walk to. Numbers nobody can
 * press would be the same lie as an invented total.
 *
 * **Neither arrow is ever hidden.** A control that vanishes at the edge of a
 * list moves the other one under the reader's cursor, so the first page bars
 * «previous» and the last bars «next» instead.
 */

/**
 * The forms a counted noun takes, supplied by the surface that owns the noun.
 *
 * Arabic has a singular, a dual, a paucal for three to ten, and a different form
 * beyond it, and one suffix serves none of the other three — «١ حالة معروضة» is
 * wrong in exactly the place a last page of one row puts it. The singular and
 * the dual carry the quantity inside the word, so no numeral is drawn beside
 * them.
 */
export interface CountNoun {
  one: string;
  two: string;
  /** Three to ten. */
  few: string;
  many: string;
}

/**
 * How many ordinals the strip draws before it starts eliding.
 *
 * Seven is the whole strip for a short list and, past that, first + last +
 * current ± 1 + two ellipses — which is the same seven boxes at every length,
 * so the control never changes width as the reader walks it.
 */
const WINDOW_LIMIT = 7;

export function Pager({
  label,
  page,
  pageCount,
  shown,
  total,
  noun,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  onGoToPage,
}: {
  /** Names this `<nav>` — «صفحات الحالات». */
  label: string;
  /** One-based. On a keyset list it is counted from the cursors walked. */
  page: number;
  /** Omitted on a keyset list, which holds no total to divide. */
  pageCount?: number | undefined;
  shown: number;
  /** Omitted on a keyset list, for the same reason. */
  total?: number | undefined;
  noun: CountNoun;
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  /**
   * Jump straight to an ordinal. **Offset lists only** — a keyset list omits it
   * and gets the walked ordinal in place of the strip.
   */
  onGoToPage?: ((target: number) => void) | undefined;
}) {
  const addressable = onGoToPage !== undefined && pageCount !== undefined && pageCount > 0;

  return (
    <nav aria-label={label} className="mt-5 grid justify-items-center gap-2">
      {/*
        **The figures, said out loud.** The line below them is drawn inside a
        `<nav>`, which is a landmark rather than a live region, so a search that
        narrowed three hundred and sixty titles to four, or a press of «التالي»
        that replaced every row on screen, changed nothing a screen reader
        announced — the reader was left to go looking for what had happened.
        Every paged register in the console shares this control, so the
        announcement belongs here and is written once.

        It restates the figures rather than adding a sentence of its own: what
        is announced and what is drawn are the same claim, and a second wording
        would be a second thing to keep true.
      */}
      <p role="status" aria-live="polite" className="sr-only">
        {copy.pager.pageLabel} <bdi>{formatNumber(page)}</bdi>
        {pageCount === undefined ? null : (
          <>
            {' '}
            {copy.pager.of} <bdi>{formatNumber(pageCount)}</bdi>
          </>
        )}
        {' — '}
        <ShownCount count={shown} noun={noun} />
        {total === undefined ? null : (
          <>
            {' '}
            {copy.pager.of} <bdi>{formatNumber(total)}</bdi>
          </>
        )}
      </p>

      <div className="flex flex-wrap items-center justify-center gap-1">
        <ArrowButton disabled={!hasPrevious} onClick={onPrevious} label={copy.pager.previous}>
          <ChevronPreviousIcon />
        </ArrowButton>

        {addressable ? (
          <ol className="m-0 flex list-none items-center gap-1 p-0">
            {pageWindow(page, pageCount).map((slot, index) =>
              slot === 'gap' ? (
                // Two elisions can appear in one strip, and neither is a page,
                // so the key is positional rather than a value they share.
                <li key={`gap-${index}`} aria-hidden="true" className="px-1 text-xs text-ink-faint">
                  {copy.pager.gap}
                </li>
              ) : (
                <li key={slot}>
                  <OrdinalButton ordinal={slot} current={slot === page} onClick={() => onGoToPage(slot)} />
                </li>
              ),
            )}
          </ol>
        ) : (
          /*
            A keyset walk states where it got to. It is drawn as text rather
            than as a barred button, because a button that can never be pressed
            reads as a control the reader has failed to earn.

            **The word and the numeral are two flex items with a `gap`, not one
            run of text.** Two separate rules conspired to glue them together —
            «صفحة١», which reads as one four-character token rather than as a
            noun and its ordinal. First, a flex container drops the leading and
            trailing whitespace of the anonymous item it wraps a bare text node
            in, so the literal space written between the label and the `<bdi>`
            never reached the box tree at all. Second, the margin that was
            added to replace it landed on the wrong side: `<bdi>` carries an
            implicit `dir="auto"`, and `auto` resolves from the first STRONG
            character — an Arabic-Indic numeral is bidi class AN, which is not
            strong, so the element resolved to `ltr` inside an `rtl` page and
            `margin-inline-start` became `margin-left`, i.e. the side facing
            away from the word. A `gap` on the container is owned by the
            container's own direction and cannot be moved by what a child
            resolves to.
          */
          <span className="hit-target flex items-center gap-1 px-3 text-xs font-semibold text-ink">
            <span>{copy.pager.pageLabel}</span>
            <bdi>{formatNumber(page)}</bdi>
          </span>
        )}

        <ArrowButton disabled={!hasNext} onClick={onNext} label={copy.pager.next}>
          <ChevronNextIcon />
        </ArrowButton>
      </div>

      {/*
        The figures under the strip, and each is its own box rather than a run of
        text beside the other. A `<bdi>` isolates a numeral from the Arabic
        around it, but two isolated numerals separated by nothing wider than a
        middle dot still land against each other — «صفحة ١ · ٢٠» reads as one
        four-digit figure, which is exactly the misreading the isolation was
        for. A real gap between two boxes cannot collapse.

        The ordinal is dropped from this line when the strip above already draws
        it: one figure said twice, a hand's width apart, is the reader being
        asked which of the two to believe.
      */}
      <p className="mb-0 flex max-w-none flex-wrap items-center justify-center gap-x-3 text-xs text-ink-muted">
        {addressable ? null : (
          <>
            <span>
              {copy.pager.pageLabel} <bdi>{formatNumber(page)}</bdi>
              {pageCount === undefined ? null : (
                <>
                  {' '}
                  {copy.pager.of} <bdi>{formatNumber(pageCount)}</bdi>
                </>
              )}
            </span>

            <span aria-hidden="true" className="text-ink-faint">
              ·
            </span>
          </>
        )}

        <span>
          <ShownCount count={shown} noun={noun} />
          {total === undefined ? null : (
            <>
              {' '}
              {copy.pager.of} <bdi>{formatNumber(total)}</bdi>
            </>
          )}
        </span>
      </p>
    </nav>
  );
}

/**
 * The ordinals the strip draws, with `'gap'` where a run was elided.
 *
 * The first and the last page are always present — they are the two
 * destinations a reader asks for by name — and the current page keeps a
 * neighbour on each side so a single press is always a single step.
 */
export function pageWindow(page: number, pageCount: number): readonly (number | 'gap')[] {
  if (pageCount <= WINDOW_LIMIT) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const slots: (number | 'gap')[] = [1];
  const first = Math.max(2, Math.min(page - 1, pageCount - 3));
  const last = Math.min(pageCount - 1, Math.max(page + 1, 4));

  if (first > 2) slots.push('gap');
  for (let ordinal = first; ordinal <= last; ordinal += 1) slots.push(ordinal);
  if (last < pageCount - 1) slots.push('gap');
  slots.push(pageCount);

  return slots;
}

/** The row count in the form the count itself requires. */
function ShownCount({ count, noun }: { count: number; noun: CountNoun }) {
  if (count === 1) return <>{noun.one}</>;
  if (count === 2) return <>{noun.two}</>;

  return (
    <>
      <bdi>{formatNumber(count)}</bdi> {count <= 10 ? noun.few : noun.many}
    </>
  );
}

/**
 * One step, in the console's own accent.
 *
 * The glyph carries the direction and the label carries the word, so the button
 * stays square at every viewport while a screen reader still hears «السابق».
 * The chevron mirrors under `dir="rtl"` with every other directional glyph in
 * the set — «previous» points at the start of the line, whichever side that is.
 */
function ArrowButton({
  disabled,
  onClick,
  label,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      title={label}
      className="hit-target flex items-center justify-center rounded-sm bg-accent px-3 text-accent-ink transition-colors duration-fast ease-standard hover:bg-accent-hover active:bg-accent-press disabled:cursor-not-allowed disabled:bg-surface-raised disabled:text-ink-faint disabled:hover:bg-surface-raised"
    >
      {children}
    </button>
  );
}

/**
 * One addressable page.
 *
 * The page in hand is `aria-current="page"` rather than `disabled`: it is where
 * the reader already is, not a destination that has been withdrawn, and a
 * disabled box in the middle of a strip reads as a page that has gone missing.
 */
function OrdinalButton({
  ordinal,
  current,
  onClick,
}: {
  ordinal: number;
  current: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={current ? 'page' : undefined}
      aria-label={`${copy.pager.pageLabel} ${formatNumber(ordinal)}`}
      className={
        current
          ? 'hit-target flex items-center justify-center rounded-sm bg-accent px-3 text-sm font-bold text-accent-ink'
          : 'hit-target flex items-center justify-center rounded-sm border border-border-strong bg-surface-raised px-3 text-sm font-semibold text-ink transition-colors duration-fast ease-standard hover:border-accent hover:text-accent-text'
      }
    >
      <bdi>{formatNumber(ordinal)}</bdi>
    </button>
  );
}
